import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { getParte, updateParte, partesApi } from '../../api/client'
import Conversacion from '../unidades/Conversacion'
import HistorialLista from '../unidades/HistorialLista'
import { formatFecha } from '../unidades/tipos'
import CerrarParteModal from './CerrarParteModal'
import CancelarParteModal from './CancelarParteModal'
import CapsulasDeParte from '../capsulas/CapsulasDeParte'
import { hoyLocal } from './fechas'
import { usePuedeEditarMantenimiento, usePuedeAbrirPartes } from '../../utils/mantenimiento'
import OtDeParte from '../ot/OtDeParte'

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200'

// Side panel for one parte: its fixed fields (editable), closing, and its
// conversation and history. `onChanged(parte)` tells the board. Editing and
// closing only for those who may edit Mantenimiento; commenting for all.
export default function PartePanel({ parteId, onClose, onChanged }) {
  const { t } = useTranslation('engineering')
  const puedeEditar = usePuedeEditarMantenimiento()
  const puedeCerrar = usePuedeAbrirPartes()      // close or cancel: also whoever holds crear_ot_mantenimiento
  const navigate = useNavigate()
  const [parte, setParte] = useState(null)
  const [tab, setTab] = useState('conversacion')
  const [error, setError] = useState('')
  const [editando, setEditando] = useState(null)     // { nombre, fecha_apertura } while editing
  const [cerrando, setCerrando] = useState(false)
  const [cancelando, setCancelando] = useState(false)
  const [recarga, setRecarga] = useState(0)          // refreshes the history after a change

  useEffect(() => {
    let vivo = true
    getParte(parteId)
      .then(p => { if (vivo) { setParte(p); setError('') } })
      .catch(() => { if (vivo) setError(t('partes.panel.notFound')) })
    return () => { vivo = false }
  }, [parteId, t])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !cerrando && !cancelando) onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, cerrando, cancelando])

  const cambiado = (p) => { setParte(p); onChanged?.(p); setRecarga(r => r + 1) }
  // The conversation says how many messages it has; the board's bubble follows.
  const cantidadComentarios = (n) => {
    if (parte && parte.n_comentarios !== n) { const p = { ...parte, n_comentarios: n }; setParte(p); onChanged?.(p) }
  }

  const guardar = async () => {
    const data = {}
    if (editando.nombre.trim() !== parte.nombre) data.nombre = editando.nombre.trim()
    if (editando.fecha_apertura !== parte.fecha_apertura) data.fecha_apertura = editando.fecha_apertura
    if (editando.implica_fs !== parte.implica_fs) {
      // On an open parte this moves the unit in or out of Mantenimiento.
      if (parte.abierto && !confirm(t(editando.implica_fs ? 'partes.fs.confirmSi' : 'partes.fs.confirmNo', { codigo: parte.unidad_codigo }))) return
      data.implica_fs = editando.implica_fs
    }
    if (!data.nombre && data.nombre !== undefined) { setError(t('partes.abrir.nameRequired')); return }
    if (data.fecha_apertura && data.fecha_apertura > hoyLocal()) { setError(t('partes.abrir.noFuture')); return }
    if (!Object.keys(data).length) { setEditando(null); return }
    try {
      cambiado(await updateParte(parte.id, data))
      setEditando(null)
      setError('')
    } catch (err) {
      const d = err.response?.data?.detail
      setError(typeof d === 'string' ? d : t('unidades.saveFailed'))
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/20" onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}>
      <aside className="w-full max-w-xl h-full bg-white shadow-2xl flex flex-col">
        <div className="px-5 pt-4 pb-3 border-b border-gray-100 space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs text-gray-400">{parte ? `${t('partes.parte')} #${parte.numero}` : '…'}</p>
              {editando ? (
                <input className={`${inputCls} mt-1`} value={editando.nombre} autoFocus
                  onChange={e => setEditando(x => ({ ...x, nombre: e.target.value }))} />
              ) : (
                <h2 className="text-lg font-semibold text-gray-900 break-words">{parte?.nombre}</h2>
              )}
            </div>
            <button type="button" onClick={onClose} title={t('unidades.cancel')}
              className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
          </div>
          {parte && (
            <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500">
              <button type="button" onClick={() => navigate(`/operations/unidades?unidad=${parte.unidad_id}`)}
                title={t('partes.panel.openUnit')} className="font-medium text-gray-800 underline decoration-dotted hover:decoration-solid">
                {parte.unidad_codigo}
              </button>
              {parte.unidad_proyecto && <span>· {parte.unidad_proyecto}</span>}
              {parte.unidad_estado && <span className="text-gray-400">({parte.unidad_estado})</span>}
              <span>·</span>
              {editando ? (
                <label className="inline-flex items-center gap-1">
                  {t('partes.campos.fecha_apertura')}
                  <input type="date" className="border border-gray-200 rounded px-1.5 py-0.5" value={editando.fecha_apertura}
                    max={parte.fecha_cierre || hoyLocal()}
                    onChange={e => setEditando(x => ({ ...x, fecha_apertura: e.target.value }))} />
                </label>
              ) : (
                <span>{t('partes.panel.opened', { fecha: formatFecha(parte.fecha_apertura), por: parte.creado_por || '—' })}</span>
              )}
              {editando ? (
                <label className="inline-flex items-center gap-1.5">
                  <input type="checkbox" checked={editando.implica_fs}
                    onChange={e => setEditando(x => ({ ...x, implica_fs: e.target.checked }))} />
                  {t('partes.campos.implica_fs')}
                </label>
              ) : parte.implica_fs
                ? <span className="inline-flex px-2 py-0.5 rounded-full font-semibold bg-red-100 text-red-800" title={t('partes.fs.badgeHint')}>F/S</span>
                : <span className="inline-flex px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{t('partes.fs.sigueOperativo')}</span>}
              {parte.abierto
                ? <span className="inline-flex px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-800">{t('partes.abierto')}</span>
                : parte.cancelado
                ? <span className="inline-flex px-2 py-0.5 rounded-full font-medium bg-gray-200 text-gray-600">{t('partes.cancelado')}</span>
                : <span className="inline-flex px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-800">
                    {t('partes.cerradoEl', { fecha: formatFecha(parte.fecha_cierre) })}
                  </span>}
            </div>
          )}
          {parte?.cancelado && (
            <p className="rounded-lg bg-gray-100 px-3 py-2 text-xs text-gray-700">
              {t('partes.cancelar.fue', { por: parte.cancelado_por || '—', fecha: formatFecha(parte.fecha_cierre) })}
              {parte.motivo_cancelacion && <> · <span className="italic">{parte.motivo_cancelacion}</span></>}
            </p>
          )}
          {parte && (puedeEditar || puedeCerrar) && (
            <div className="flex items-center gap-3 pt-1">
              {!puedeEditar ? null : editando ? (
                <>
                  <button type="button" onClick={guardar} className="text-xs font-semibold text-gray-900 hover:underline">{t('unidades.save')}</button>
                  <button type="button" onClick={() => { setEditando(null); setError('') }} className="text-xs text-gray-500 hover:text-gray-900">{t('unidades.cancel')}</button>
                </>
              ) : (
                <button type="button" onClick={() => setEditando({ nombre: parte.nombre, fecha_apertura: parte.fecha_apertura, implica_fs: parte.implica_fs })}
                  className="text-xs font-medium text-gray-500 hover:text-gray-900">{t('unidades.edit')}</button>
              )}
              {parte.abierto && !editando && (
                <button type="button" onClick={() => setCerrando(true)}
                  className="bg-gray-900 text-white px-3 py-1 rounded-lg text-xs font-medium hover:bg-gray-800">
                  {t('partes.cerrar.button')}
                </button>
              )}
              {parte.abierto && !editando && (
                <button type="button" onClick={() => setCancelando(true)} title={t('partes.cancelar.hint')}
                  className="ml-auto border border-rose-200 text-rose-700 px-3 py-1 rounded-lg text-xs font-medium hover:bg-rose-50">
                  {t('partes.cancelar.button')}
                </button>
              )}
            </div>
          )}
          {error && <p className="text-red-500 text-xs">{error}</p>}
          {parte?.ot && (
            <OtDeParte parte={parte}
              onCambio={o => { const p = { ...parte, ot: { ...parte.ot, estado: o.estado } }; setParte(p); onChanged?.(p) }} />
          )}
          <div className="flex gap-1 pt-1">
            {['conversacion', 'trabajo', 'historial'].map(k => (
              <button key={k} type="button" onClick={() => setTab(k)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  tab === k ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'}`}>
                {k === 'trabajo' ? t('capsulas.tabParte') : t(`unidades.panel.${k}`)}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 min-h-0 px-5 py-4 overflow-hidden">
          {tab === 'conversacion' ? <Conversacion unidadId={parteId} api={partesApi} onCantidad={cantidadComentarios} />
            : tab === 'trabajo' ? (parte ? <CapsulasDeParte key={`${parte.id}-${parte.abierto}`} parte={parte} /> : null)
            : <div className="h-full overflow-y-auto"><HistorialLista parteId={parteId} recarga={recarga} /></div>}
        </div>
      </aside>
      {cerrando && parte && (
        <CerrarParteModal parte={parte} onClose={() => setCerrando(false)} onClosed={cambiado} />
      )}
      {cancelando && parte && (
        <CancelarParteModal parte={parte} onClose={() => setCancelando(false)} onCancelled={cambiado} />
      )}
    </div>
  )
}
